import i18next from 'i18next'
import { List } from 'immutable'
import React from 'react'
import { Alert, Form } from 'react-bootstrap'
import { options } from 'authentication/auth_widget/utils'
import { initialCompanyState } from 'forms/company/companyState'
import { companyWidget } from 'forms/company/companyWidget'
import {
  IDealIssuer,
  PaymentType,
  paymentTypeLabel,
} from 'forms/paymentMethod/paymentMethodState'
import { initialPersonalInfoState } from 'forms/personalInfo/personalInfoState'
import { personalInfoWidget } from 'forms/personalInfo/personalInfoWidget'
import { TextPageHeader } from 'site/pageHeader/pageHeaderLayout'
import { authOptions } from 'site/rootWidget'
import { routeUpdaters } from 'site/routes/routesState'
import { BreadcrumbItem } from 'utils/breadcrumbs'
import { DataLayer, initializePIIData, PIIData } from 'utils/datalayer'
import {
  AuthButtonPrimary,
  AuthButtonSecondary,
  FormControls,
  FormNotification,
  FormSkeletonLayout,
  formSkeletonConfig,
} from 'utils/forms'
import { ComponentDidMount } from 'utils/lifecycleMethods'
import { SVGIcon } from 'utils/svgicon'
import { capitalize, getClassName } from 'utils/utils'
import { SingleValidatorResult } from 'validator/validatorExpr'
import {
    LookupData,
    OpenPayment,
    Subscription,
    SubscriptionRedirect,
    SubscriptionState,
    subscriptionUpdaters,
    UserSubscriptions,
} from './subscriptionState'
import { formatSubscriptionPrice } from './subscriptionUtils'
import { navigate, Updater } from 'widgets-for-react'
import successUpgradeSubscriptionWidget from './successUpgradeSubscription/successUpgradeSubscriptionWidget'

export const SubscriptionLayout = {
  Wrapper: (props: {
    id?: string
    breadcrumbs: List<BreadcrumbItem>
    children: JSX.Element | JSX.Element[]
  }) => (
    <>
      <TextPageHeader
        title={i18next.t('subscription:accountDetailsTitle')}
        intro={i18next.t('subscription:accountDetailsIntro')}
        breadcrumbs={props.breadcrumbs}
      />

      <section id={props.id} className="account">
        <div className="account__inner container--m gutter--m">
          {props.children}
        </div>
      </section>
    </>
  ),
  Section: (props: {
    title: string
    children: JSX.Element | JSX.Element[]
    suppressHotjar?: boolean
  }) => (
    <section
      className={`account__section text--styled ${
        props.suppressHotjar == true ? 'data-hj-suppress' : ''
      }`}
    >
      <h2 className="account__subtitle">{props.title}</h2>
      {props.children}
    </section>
  ),
  Row: (props: {
    id: string
    onSubmit?: () => void
    onClick?: () => void
    children: JSX.Element | JSX.Element[]
  }) => {
    const el = document.getElementById(props.id)
    props.onClick &&
      el?.addEventListener('keypress', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          props.onClick && props.onClick()
        }
      })
    return props.onSubmit ? (
      <form
        id={props.id}
        onSubmit={props.onSubmit || undefined}
        onClick={props.onClick || undefined}
        className="account__row account__row--editing"
        tabIndex={props.onClick ? 0 : -1}
      >
        <div className="account__row-inner">{props.children}</div>
      </form>
    ) : (
      <div
        id={props.id}
        onClick={props.onClick || undefined}
        tabIndex={props.onClick ? 0 : -1}
        className="account__row"
      >
        <div className="account__row-inner">{props.children}</div>
      </div>
    )
  },
  Col: (props: { kind: 'label' | 'value'; children: React.ReactNode }) => (
    <div className={`account__col account__col--${props.kind}`}>
      {props.children}
    </div>
  ),

  EditButton: (props: {
    srText?: string
    onClick: (_?: any) => void
    disabled?: boolean
  }) => (
    <button
      type="button"
      className="account-button account-button--edit"
      title={props.srText || i18next.t('subscription:Edit')}
      onClick={props.onClick}
      disabled={props.disabled || false}
      tabIndex={-1}
    >
      <span className="sr-text">
        {props.srText || i18next.t('subscription:Edit')}
      </span>
      <SVGIcon className="account-button__icon" size={22} iconId="edit" />
    </button>
  ),
  CloseButton: (props: {
    srText?: string
    onClick: (_?: any) => void
    disabled?: boolean
  }) => (
    <button
      type="button"
      aria-hidden={true}
      className="account-button account-button--close"
      title={props.srText || i18next.t('subscription:Cancel')}
      onClick={props.onClick}
      disabled={props.disabled || false}
      tabIndex={-1}
    >
      <span className="sr-text">
        {props.srText || i18next.t('subscription:Cancel')}
      </span>
      <SVGIcon className="account-button__icon" size={24} iconId="close" />
    </button>
  ),

  NewSubscriptionProgressBar: (props: { activeStep: 1 | 2 | 3 }) => {
    const StepLabel = (props: { count: number }) => (
      <>
        <span className="step__label--a">
          {i18next.t('subscription:Step')}{' '}
        </span>
        <span className="step__label--b">{props.count}</span>
        <span className="step__label--c">
          {i18next.t(`subscription:Step${props.count}`)}
        </span>
      </>
    )
    return (
      <ol className="steps steps--3">
        {/* Subscription */}
        <li
          aria-controls="a11y-trial-to-pro-step-1"
          aria-hidden={props.activeStep !== 1}
          className={`step ${
            props.activeStep > 1 ? 'step--completed' : 'step--active'
          }`}
        >
          <StepLabel count={1} />
        </li>
        {/* Details */}
        <li
          aria-controls="a11y-trial-to-pro-step-2"
          aria-hidden={props.activeStep !== 2}
          className={`step ${
            props.activeStep > 2
              ? 'step--completed'
              : props.activeStep < 2
              ? 'step--disabled'
              : 'step--active'
          }`}
        >
          <StepLabel count={2} />
        </li>
        {/* Payment */}
        <li
          aria-controls="a11y-trial-to-pro-step-3"
          aria-hidden={props.activeStep !== 3}
          className={`step ${
            props.activeStep !== 3 ? 'step--disabled' : 'step--active'
          }`}
        >
          <StepLabel count={3} />
        </li>
      </ol>
    )
  },

  RedirectToPsp: (props: {
    variant: 'new subscription flow' | 'other'
    redirectUrl: string
  }) => {
    const RedirectLink = (
      <a
        href={props.redirectUrl}
        target="_self"
        className="button--primary button--s"
      >
        <span className="button__label">
          {i18next.t('subscription:RedirectToPspNow')}
        </span>
      </a>
    )
    return props.variant == 'new subscription flow' ? (
      <FormSkeletonLayout
        type="redirect-to-psp"
        kind="text-image"
        options={authOptions}
        isClrInverted={true}
        image={formSkeletonConfig.imageDark}
      >
        <header
          className={getClassName(authOptions, 'headerClassName', 'mb-4')}
        >
          <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
            {i18next.t('subscription:PspRedirectTitle')}
          </h1>
          <p className={getClassName(authOptions, 'introClassName', 'lead')}>
            {i18next.t('subscription:PspRedirectMessage')}
          </p>
        </header>

        <div className={getClassName(authOptions, 'formActionsClassName', '')}>
          <div
            className={getClassName(
              authOptions,
              'clickableWrapperClassName',
              ''
            )}
          >
            {RedirectLink}
          </div>
        </div>
      </FormSkeletonLayout>
    ) : (
      <div className="text-screen">
        <TextPageHeader
          title={i18next.t('subscription:PspRedirectTitle')}
          intro={i18next.t('subscription:PspRedirectMessage')}
          breadcrumbs={undefined}
          additionalJSX={
            <div className="block__clickables">
              <div className="block__clickable">{RedirectLink}</div>
            </div>
          }
        />
      </div>
    )
  },

  ThankYou: (props: {
    variant: SubscriptionRedirect | 'stop-subscription-success'
    subscriptionState: SubscriptionState
    thankYouTitle?: string
    thankYouMessage?: string
    buttonLabel?: string
    breadcrumbs?: List<BreadcrumbItem>
    setState?: (_: Updater<SubscriptionState>) => void;
  }) => {
    let Title
    let Message
    let Button
    switch (props.variant) {
      case 'stop-subscription-success':
        Title = i18next.t('subscription:SubscriptionStoppedThankYouTitle')
        Message = i18next.t('subscription:SubscriptionStoppedThankYouMessage')
        Button = i18next.t('paywall:paywallSubscribeButton')
        break
      case 'change-payment-method-success':
        Title = i18next.t('subscription:PaymentMethodChangedThankYouTitle')
        Message = i18next.t('subscription:PaymentMethodChangedThankYouMessage')
        Button = i18next.t('subscription:BackToSubscriptions')
        break
      case 'pay-open-invoice-success':
        Title = i18next.t('subscription:OpenInvoicePaidThankYouTitle')
        Message = i18next.t('subscription:OpenInvoicePaidThankYouMessage')
        break
    }
    return props.variant == 'subscription-success' ? (
      <>
        <FormSkeletonLayout
          type="trial-to-pro-success"
          kind="text-image"
          options={authOptions}
          isClrInverted={true}
          image={formSkeletonConfig.imageDark}
        >
          {props.setState && successUpgradeSubscriptionWidget()(props.subscriptionState)
            .map(subscriptionUpdaters.updateSubscriptionState)
            .run(props.setState)
          }
        </FormSkeletonLayout>
      </>
    ) : (
      <div className="text-screen">
        <TextPageHeader
          title={Title || i18next.t('subscription:ThankYou')}
          intro={Message || undefined}
          breadcrumbs={props.breadcrumbs || undefined}
          additionalJSX={
            <div className="block__clickables">
              <div className="block__clickable">
                <AuthButtonPrimary
                  kind="link"
                  to={routeUpdaters(i18next.language).accountDetails.url}
                  label={
                    Button ||
                    props.buttonLabel ||
                    i18next.t('subscription:BackToSubscriptions')
                  }
                  disabled={false}
                />
              </div>
            </div>
          }
        />
      </div>
    )
  },

  FailureOrCancel: (props: {
    variant: SubscriptionRedirect | 'change payment method flow' | 'overview'
  }) => {
    return props.variant == 'subscription-failure' ||
      props.variant === 'subscription-cancel' ? (
      <>
        <ComponentDidMount
          onMount={() => {
            DataLayer.upgradeFlowStepError(3, 'betaling niet succesvol')
          }}
        />
        <FormSkeletonLayout
          type="trial-to-pro-failed"
          kind="text-image"
          options={authOptions}
          isClrInverted={true}
          image={formSkeletonConfig.imageDark}
        >
          <header
            className={getClassName(authOptions, 'headerClassName', 'mb-4')}
          >
            <h1 className={getClassName(authOptions, 'titleClassName', 'mb-4')}>
              {i18next.t('subscription:Oops')}
            </h1>
            <p className={getClassName(authOptions, 'introClassName', 'lead')}>
              {i18next.t('subscription:PaymentErrorMessage')}
            </p>
          </header>

          <div
            className={getClassName(authOptions, 'formActionsClassName', '')}
          >
            <div
              className={getClassName(
                authOptions,
                'clickableWrapperClassName',
                ''
              )}
            >
              <AuthButtonPrimary
                kind="link"
                to={routeUpdaters(i18next.language).upgrade.url}
                label={i18next.t('subscription:BackToStep1')}
                disabled={false}
              />
            </div>
          </div>
        </FormSkeletonLayout>
      </>
    ) : (
      <div className="text-screen">
        <TextPageHeader
          title={i18next.t('subscription:Oops')}
          intro={i18next.t('subscription:PaymentErrorMessage')}
          breadcrumbs={undefined}
        />
        <div className="block block--text">
          <div className="block__inner container--m gutter--m">
            <div className="block__clickables">
              <div className="block__clickable">
                <AuthButtonPrimary
                  kind="link"
                  to={routeUpdaters(i18next.language).accountDetails.url}
                  label={i18next.t('subscription:BackToSubscriptions')}
                  disabled={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },

  ActionAndBackButtons: (props: {
    back: () => void
    action: () => void
    actionDisabled: boolean
    actionText: string
    reverseButtons?: boolean
  }) => {
    const PrimaryButtonWrapper = props.reverseButtons
      ? AuthButtonSecondary
      : AuthButtonPrimary
    const SecondaryButtonWrapper = props.reverseButtons
      ? AuthButtonPrimary
      : AuthButtonSecondary
    const PrimaryButton = (
      <PrimaryButtonWrapper
        kind="button"
        type="button"
        disabled={props.actionDisabled}
        onClick={(e) => props.action()}
        label={props.actionText}
      />
    )
    const SecondaryButton = (
      <SecondaryButtonWrapper
        kind="button"
        type="button"
        disabled={false}
        onClick={(_) => props.back()}
        label={i18next.t('subscription:Back')}
      />
    )
    return (
      <div className="block__footer block__clickables">
        <div className="block__clickable">
          {props.reverseButtons ? SecondaryButton : PrimaryButton}
        </div>
        <div className="block__clickable">
          {props.reverseButtons ? PrimaryButton : SecondaryButton}
        </div>
      </div>
    )
  },
  FormSkeletonActionAndBackButtons: (props: {
    back?: () => void
    action: () => void
    actionDisabled: boolean
    actionText: string
  }) => (
    <div className={getClassName(authOptions, 'formActionsClassName', '')}>
      <div
        className={getClassName(authOptions, 'clickableWrapperClassName', '')}
      >
        <AuthButtonPrimary
          kind="button"
          type="submit"
          disabled={props.actionDisabled}
          label={props.actionText}
          onClick={(e) => {
            e.preventDefault()
            props.action()
          }}
        />
      </div>
      {props.back && (
        <div
          className={getClassName(authOptions, 'clickableWrapperClassName', '')}
        >
          <AuthButtonSecondary
            kind="button"
            type="button"
            disabled={false}
            label={i18next.t('subscription:Back')}
            onClick={props.back}
          />
        </div>
      )}
    </div>
  ),

  NewSubscriptionButtons: (props: {
    subscribe: () => void
    changePaymentMethod: () => void
    subscribeDisabled: boolean
    showNewSubscription: boolean
    showChangePaymentMethod: boolean
  }) => (
    <div className="block__clickables">
      {props.showNewSubscription && (
        <div className="block__clickable">
          <AuthButtonPrimary
            kind="button"
            type="button"
            disabled={props.subscribeDisabled}
            onClick={(e) => props.subscribe()}
            label={i18next.t('subscription:SubscribeNow')}
          />
        </div>
      )}

      {props.showChangePaymentMethod && (
        <div className="block__clickable">
          <AuthButtonPrimary
            kind="button"
            type="button"
            disabled={false}
            onClick={(e) => props.changePaymentMethod()}
            label={i18next.t('subscription:ChangePaymentMethod')}
          />
        </div>
      )}
    </div>
  ),

  BlockedAccount: (props: {}) => (
    <section className="blocked-account">
      <div className="blocked-account__inner container--m gutter--m">
        <h1 className="blocked-account__title title--m">
          <span>{i18next.t('subscription:AccountBlocked')}</span>
          <svg
            aria-hidden="true"
            focusable="false"
            data-prefix="fas"
            data-icon="exclamation-triangle"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 576 512"
            style={{
              display: 'inline-block',
              fontSize: 'inherit',
              height: '1em',
              overflow: 'visible',
              verticalAlign: '-0.125em',
            }}
          >
            <path
              fill="currentColor"
              d="M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028zM288 354c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418 136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884 0-12.356 5.78-11.981 12.654z"
            ></path>
          </svg>
        </h1>
        <p className="blocked-account__text text--styled">
          {i18next.t('subscription:AccountBlockedMessage')}
        </p>
      </div>
    </section>
  ),
  AccountType: (props: {
    kind:
      | 'stopped-or-none'
      | 'will-be-stopped'
      | 'trial'
      | 'pro'
      | 'student'
      | 'student-paused-pro'
      | 'teacher'
      | 'teacher-paused-pro'
    intro: JSX.Element
  }) => (
    <SubscriptionLayout.Row id="account-type">
      <SubscriptionLayout.Col kind="label">
        <span className="sr-text">
          {`${i18next.t('subscription:YourCurrentSubscription')}: `}
        </span>
        <span>
          {props.kind === 'stopped-or-none'
            ? i18next.t('subscription:StoppedSubscription')
            : props.kind === 'will-be-stopped'
            ? i18next.t('subscription:WillBeStoppedSubscription')
            : props.kind === 'student'
            ? i18next.t('subscription:StudentSubscription')
            : props.kind === 'student-paused-pro'
            ? i18next.t(
                'subscription:StudentSubscriptionWithPausedActiveAccount'
              )
            : props.kind === 'teacher'
            ? i18next.t('subscription:TeacherSubscription')
            : props.kind === 'teacher-paused-pro'
            ? i18next.t(
                'subscription:TeacherSubscriptionWithPausedActiveAccount'
              )
            : props.kind === 'trial'
            ? i18next.t('subscription:TrialSubscription')
            : props.kind === 'pro'
            ? i18next.t('subscription:ProSubscription')
            : ''}
        </span>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        {props.intro}
        {props.kind !== 'pro' &&
          !props.kind.includes('student') &&
          !props.kind.includes('teacher') && (
            <AuthButtonPrimary
              kind="link"
              to={routeUpdaters(i18next.language).upgrade.url}
              disabled={false}
              label={i18next.t('subscription:upgradeAccount')}
            />
          )}
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),

  PaymentMethodStillValidAlert: (props: {}) => (
    <Alert variant="info">
      {i18next.t('subscription:PaymentMethodStillValid')}
    </Alert>
  ),

  OpenInvoice: (props: { options: options; children: any }) => (
    <SubscriptionLayout.Row id="account-open-invoice">
      <SubscriptionLayout.Col kind="label">
        <span>{i18next.t('subscription:OpenInvoices')}</span>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        <FormNotification
          options={props.options}
          extraClasses="account__alert"
          message={i18next.t('subscription:YouHaveOpenInvoicesNotification')}
        />
        <ul className="account__invoice-list">{props.children}</ul>
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),
  OpenInvoiceItem: (props: { openPayment: OpenPayment; onPay: () => void }) => (
    <li className="account__invoice-item">
      <div className="account__invoice-row">
        <div className="account__invoice-col">
          <span>
            <b>{i18next.t(props.openPayment.Title.split(' - ')[1])}</b>
          </span>
          <span className="text--s">
            {i18next.t('subscription:DaysLeftToPay', {
              days: Math.ceil(
                (new Date(props.openPayment.PaymentDate).getTime() -
                  Date.now()) /
                  (1000 * 60 * 60 * 24)
              ),
            })}
          </span>
        </div>
        <div className="account__invoice-col">
          <span>
            <b>{`€ ${props.openPayment.Amount}`}</b>
          </span>
        </div>
      </div>

      <AuthButtonPrimary
        kind="button"
        type="button"
        disabled={false}
        onClick={(_) => props.onPay()}
        label={i18next.t('subscription:PayOpenInvoice')}
      />
    </li>
  ),

  Email: (props: { email: string }) => (
    <SubscriptionLayout.Row id="account-email">
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('auth:email')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        <b>{props.email}</b>
        <p className="text--s">{i18next.t('subscription:ChangeEmailText')}</p>
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),
  ChangePassword: (props: {
    onSubmit?: () => void
    onClick?: () => void
    children: JSX.Element | JSX.Element[]
  }) => (
    <SubscriptionLayout.Row
      id="change-password"
      onSubmit={props.onSubmit}
      onClick={props.onClick}
    >
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('subscription:ChangePassword')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        {props.children}
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),
  ChangePasswordButtons: (props: {
    save: () => void
    cancel: () => void
    changePasswordDisabled: boolean
  }) => (
    <div className="block__clickables">
      <div className="block__clickable">
        <AuthButtonPrimary
          kind="button"
          type="submit"
          disabled={props.changePasswordDisabled}
          onClick={(_) => props.save()}
          label={i18next.t('subscription:ChangePassword')}
        />
      </div>
      <div className="block__clickable">
        <AuthButtonSecondary
          kind="button"
          type="button"
          disabled={false}
          onClick={(e) => props.cancel()}
          label={i18next.t('subscription:Cancel')}
        />
        <SubscriptionLayout.CloseButton onClick={props.cancel} />
      </div>
    </div>
  ),
  ChangePasswordPlaceholder: (props: { edit: () => void }) => (
    <>
      <span aria-hidden="true">*************</span>
      <SubscriptionLayout.EditButton onClick={props.edit} />
    </>
  ),

  PersonalInfo: (props: {
    onSubmit?: () => void
    onClick?: () => void
    children: JSX.Element | JSX.Element[]
  }) => (
    <SubscriptionLayout.Row
      onSubmit={props.onSubmit}
      onClick={props.onClick}
      id="account-personal-info"
    >
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('subscription:PersonalInfo')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        {props.children}
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),
  PersonalInfoPlaceholder: ({
    userSubscriptions: {
      User: userData,
      IsStripeCustomer
    },
    lookupData,
    edit,
    isStudent
  }: {
    userSubscriptions: UserSubscriptions
    lookupData: LookupData
    edit: () => void
    isStudent: boolean
  }) => (
    <div className="account__show-plain-text-forms">
      {personalInfoWidget(
        lookupData.Languages,
        lookupData.Countries,
        lookupData.Jobs,
        true,
        true,
        false,
        isStudent,
        true
      )(initialPersonalInfoState(userData)).run(() => {})}
      {!isStudent && !IsStripeCustomer && (
        <>
          <Form.Label
            className={getClassName(authOptions, 'formLabelClassName', '')}
          >
            {i18next.t('subscription:InvoiceToCompany')}
          </Form.Label>
          {!!userData && !!userData.Company ? (
            <p>{i18next.t('subscription:InvoiceWithCompany')}</p>
          ) : (
            <p>{i18next.t('subscription:InvoiceWithoutCompany')}</p>
          )}
        </>
      )}

      {!!userData && !!userData.Company ? (
        companyWidget(
          lookupData.Countries,
          lookupData.CompanyTypes,
          true,
          true,
          true
        )(initialCompanyState(userData)).run((_) => {})
      ) : (
        <></>
      )}
      <SubscriptionLayout.EditButton onClick={edit} />
    </div>
  ),
  PersonalInfoButtons: (props: {
    save: () => void
    cancel: () => void
    saveDisabled: boolean
  }) => (
    <div className="block__clickables">
      <div className="block__clickable">
        <AuthButtonPrimary
          kind="button"
          type="submit"
          disabled={props.saveDisabled}
          onClick={(e) => props.save()}
          label={i18next.t('subscription:SavePersonalInfo')}
        />
      </div>
      <div className="block__clickable">
        <AuthButtonSecondary
          kind="button"
          type="button"
          disabled={false}
          onClick={(e) => props.cancel()}
          label={i18next.t('subscription:Cancel')}
        />
        <SubscriptionLayout.CloseButton onClick={props.cancel} />
      </div>
    </div>
  ),
  PersonalInfoButtonsWithRouter: (props: { save: () => void, onSaveGoTo?: string, cancel: () => void, onCancelGoTo?: string, saveDisabled: boolean }) => (
    <div className="block__clickables">
      <div className="block__clickable">
        <AuthButtonPrimary
          kind="button"
          type="submit"
          disabled={props.saveDisabled}
          onClick={e => {
            e.preventDefault()
            props.save()
            if (props.onSaveGoTo) navigate(props.onSaveGoTo)
          }}
          label={i18next.t("subscription:SavePersonalInfo")}
        />
      </div>
      <div className="block__clickable">
        <AuthButtonSecondary
          kind="button"
          type="button"
          disabled={false}
          onClick={e => {
            props.cancel()
            if (props.onCancelGoTo) navigate(props.onCancelGoTo)
          }}
          label={i18next.t("subscription:Cancel")}
        />
        <SubscriptionLayout.CloseButton onClick={props.cancel} />
      </div>
    </div>
  ),
  ChangePaymentPeriod: (props: {
    onSubmit?: () => void
    onClick?: () => void
    children: JSX.Element | JSX.Element[]
  }) => (
    <SubscriptionLayout.Row
      id="account-current-payment-method"
      onSubmit={props.onSubmit}
      onClick={props.onClick}
    >
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('subscription:ChangePaymentPeriod')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        {props.children}
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),
  ChangePaymentPeriodPlaceholder: (props: {
    edit: () => void
    options: options
    paymentMethodInvalid: boolean
    paymentFrequency: string
    amount: number
    currency: string
    nextPayment: string
  }) => (
    <>
      {props.paymentMethodInvalid && (
        <FormNotification
          options={props.options}
          extraClasses="account__alert"
          message={i18next.t(
            'subscription:NoAvailablePaymentMethodPleaseChange'
          )}
        />
      )}

      <p>{i18next.t('subscription:ChangePaymentPeriodIntro')}</p>

      {i18next.language === 'nl' ? <p>Tijdelijk niet beschikbaar</p> : <p>Current unavailable</p>}

      <SubscriptionLayout.EditButton
        onClick={(_) => props.edit()}
        disabled={true} //{props.paymentMethodInvalid}
      />
    </>
  ),

  ChangePaymentPeriodButtons: (props: {
    save: () => void
    cancel: () => void
    saveDisabled: boolean
  }) => (
    <div className="block__clickables">
      <div className="block__clickable">
        <AuthButtonPrimary
          kind="button"
          type="submit"
          disabled={props.saveDisabled}
          onClick={(e) => props.save()}
          label={i18next.t('subscription:ChangePaymentPeriod')}
        />
      </div>
      <div className="block__clickable">
        <AuthButtonSecondary
          kind="button"
          type="button"
          disabled={false}
          onClick={(_) => props.cancel()}
          label={i18next.t('subscription:Cancel')}
        />
        <SubscriptionLayout.CloseButton onClick={props.cancel} />
      </div>
    </div>
  ),

  ChangePaymentMethod: (props: {
    paymentMethodInvalid: boolean
    edit: () => void
  }) => (
    <SubscriptionLayout.Row id="account-change-payment-method">
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('subscription:ChangePaymentMethodTitle')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        <p>{i18next.t('subscription:ChangePaymentMethodIntro')}</p>

        {i18next.language === 'nl' ? <p>Tijdelijk niet beschikbaar</p> : <p>Current unavailable</p>}

        <button className="link--styled" disabled={true} onClick={(_) => props.edit()}>
          {props.paymentMethodInvalid
            ? i18next.t('subscription:AddPaymentMethod')
            : i18next.t('subscription:ChangePaymentMethodTitle')}
        </button>
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),

  StopCurrentSubscription: (props: { to: string }) => (
    <SubscriptionLayout.Row id="account-stop-sub">
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('subscription:StopCurrentSubscription')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        <p>{i18next.t('subscription:StopCurrentSubscriptionText')}</p>

        {i18next.language === 'nl' ? <p>Tijdelijk niet beschikbaar</p> : <p>Current unavailable</p>}
        {
        //<Link to={props.to}>{i18next.t('subscription:StopSubscriptionButton')}</Link>
        }
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),

  DeleteAccount: (props: {}) => (
    <SubscriptionLayout.Row id="account-delete">
      <SubscriptionLayout.Col kind="label">
        <p>{i18next.t('subscription:DeleteAccount')}</p>
      </SubscriptionLayout.Col>
      <SubscriptionLayout.Col kind="value">
        <p>{i18next.t('subscription:DeleteAccountText')}</p>
      </SubscriptionLayout.Col>
    </SubscriptionLayout.Row>
  ),

  SubscriptionChoice: (props: {
    kind: 'account-details' | 'trial-to-pro'
    select: (s: Subscription) => void
    availableSubscriptions: Array<Subscription>
    currentSubscription?: Subscription
  }) => {
    const saveMoneyTranslation = (currency: string) =>
      i18next
        .t('subscription:SaveMoney', { amount: `${currency} 19!` })
        .toLowerCase()

    const cancelAnytimeTranslation = () =>
      i18next.t('subscription:CancelAnytime').toLowerCase()

    const PaymentSubTitleTranslation = (
      paymentFrequency: 'monthly' | 'yearly' | undefined,
      currency: string | undefined
    ): string => {
      switch (paymentFrequency) {
        case 'yearly':
          return ` (${saveMoneyTranslation(currency || '€')})`
        case 'monthly':
          return ` (${cancelAnytimeTranslation()})`
        case undefined:
          return ''
      }
    }

    return (
      <fieldset className="account__bools styled-bools sb sb--ghost sb--radio">
        <legend className={props.kind === 'trial-to-pro' ? 'title--s' : ''}>
          {props.kind === 'trial-to-pro' &&
            `${i18next.t('subscription:ChooseYourSubscription')}:`}
          {props.kind === 'account-details' &&
            `${i18next.t('subscription:SelectYourPreferredPaymentPeriod')}:`}
        </legend>

        {props.availableSubscriptions.map((s) => {
          // Replace any / and : characters with the - character to turn the backend output into a valid CSS identifier
          const id = `sb-${s.Title?.replace(/[/:]/g, '-')}`

          return (
            <div className="sb__bool" key={s.PaymentFrequency}>
              <input
                type="radio"
                name="sb-subscription"
                className="sb__input form-check-input"
                checked={
                  s.Title == props.currentSubscription?.Title &&
                  s.Title != undefined
                }
                disabled={
                  s.Title == props.currentSubscription?.Title &&
                  s.Title != undefined
                }
                onChange={(e) => props.select(s)}
                id={id}
              />
              <label
                className="sb__label"
                htmlFor={id}
              >
                <span
                  className={`sb__title ${
                    props.kind === 'trial-to-pro' ? 'title--xs' : ''
                  }`}
                >
                  {capitalize(
                    i18next.t(`subscription:${s.PaymentFrequency?.toLowerCase()}`)
                  )}
                </span>
                <span className="sb__subtitle">
                  {`${s.Currency} ${
                    s.PaymentFrequency?.toLowerCase().includes('month')
                      ? formatSubscriptionPrice(s.Price || 0, i18next.language)
                      : s.Price
                  } /${i18next.t(
                    `subscription:${
                      s.PaymentFrequency?.toLowerCase().includes('month')
                        ? 'month'
                        : 'year'
                    }`
                  )}${PaymentSubTitleTranslation(
                    s.PaymentFrequency !== undefined
                      ? s.PaymentFrequency.toLowerCase().includes('month')
                        ? 'monthly'
                        : s.PaymentFrequency.toLowerCase().includes('year')
                        ? 'yearly'
                        : undefined
                      : undefined,
                    s.Currency
                  )}`}
                </span>
              </label>
            </div>
          )
        })
      }
      </fieldset>
    )
  },
  PaymentMethodChoice: (props: {
    select: (s: PaymentType) => void
    selectIdealIssuer: (s: IDealIssuer) => void
    paymentMethods: Array<PaymentType>
    iDealIssuers: Array<IDealIssuer>
    currentIdealIssuer?: string
    currentBank?: string
    IdealIssuerValidation: SingleValidatorResult
  }) => (
    <fieldset className="account__bools styled-bools sb sb--ghost sb--radio sb--logos">
      <legend className="sr-text">
        {i18next.t('auth:selectPlaceholder', {
          entity: i18next.t('subscription:PaymentMethod').toLowerCase(),
        })}
      </legend>

      <div className="sb__row">
        {props.paymentMethods.map((b) => (
          <div className="sb__bool" key={b}>
            <input
              type="radio"
              name="sb-paymentMethod"
              className="sb__input form-check-input"
              checked={b == props.currentBank && b != undefined}
              disabled={b == props.currentBank && b != undefined}
              onChange={(e) => props.select(b)}
              id={`sb-${b}`}
            />
            <label className="sb__label" htmlFor={`sb-${b}`}>
              <div className="sb__label-inner">
                <span className="sb__title">{paymentTypeLabel(b)}</span>
                <img
                  src={`/spa/images/icons/banks/${paymentTypeLabel(
                    b
                  ).toLowerCase()}.svg`}
                  alt=""
                  className="sb__image lazyload"
                  width="50"
                  height="30"
                />
              </div>

              {/* Render iDEAL banks dropdown in boolean */}
              {b.toLowerCase() == 'ideal' &&
              props.currentBank?.toLowerCase() === 'ideal' ? (
                <FormControls.FormSelectInGroup
                  key="checkoutIDealIssuer"
                  controlId="iDealIssuer"
                  label={i18next.t('subscription:Bank')}
                  value={props.currentIdealIssuer || ''}
                  required={true}
                  onChange={(newValue) =>
                    props.selectIdealIssuer(
                      props.iDealIssuers.find((i) => i.Title == newValue) ||
                        props.iDealIssuers[0]
                    )
                  }
                  validation={props.IdealIssuerValidation}
                >
                  {[
                    <option value="" disabled={true}>
                      {i18next.t('auth:selectPlaceholder', {
                        entity: i18next.t('subscription:Bank').toLowerCase(),
                      })}
                    </option>,
                    ...props.iDealIssuers.map((i) => (
                      <option value={i.Title || ''}>{i.Title}</option>
                    )),
                  ]}
                </FormControls.FormSelectInGroup>
              ) : (
                <></>
              )}
            </label>
          </div>
        ))}
      </div>
    </fieldset>
  ),
}
